import React, { useState, useEffect, useMemo } from "react";
import {
  Search,
  Download,
  MessageSquare,
  CheckCircle,
  MousePointer,
  XCircle,
  Clock,
  BarChart3,
  Users,
  Zap,
  AlertCircle,
} from "lucide-react";
import { useParams, useNavigate } from "react-router-dom";
import useRequest from "../../components/hook/use-req";
import { Campaign } from "../campaign/types";
import { AnalyticsResponse, DeliveryReport } from "./types";
interface MetricCardProps {
  title: string;
  value: number;
  percentage: number;
  icon: React.ElementType;
  color: string;
  delay: number;
}

const SMSCampaignReport = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const token = localStorage.getItem("authToken");
  const [DLReport, setDLReport] = useState<DeliveryReport[]>([]);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [animationStage, setAnimationStage] = useState(0);
  const [currentCursor, setCurrentCursor] = useState<number | null>(null);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const [previousCursors, setPreviousCursors] = useState<(number | null)[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [analytics, setAnalytics] = useState<AnalyticsResponse | null>(null);
  const itemsPerPage = 10;

  const { makeRequest: getReport } = useRequest(`/campaign/dlrlog`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  const { makeRequest: getSummary } = useRequest(`/campaign/analytics`, "GET", {
    Authorization: `Bearer ${token}`,
  });

  useEffect(() => {
    const fetchData = async () => {
      const [response] = await getSummary(undefined, {
        campaign_id: id
      });
      setAnalytics(response);
    };
    fetchData();
  }, []);




  // Fetch DLR data
  const fetchData = async (cursorValue: number | null = null) => {
    try {
      setLoading(true);
      const params: any = {
        campaign_id: id,
        limit: itemsPerPage,
      };

      if (cursorValue !== null) {
        params.cursor = cursorValue;
      }
      const [response] = await getReport(undefined, params);

      let reportData: DeliveryReport[] = [];

      if (response?.dlrs && Array.isArray(response.dlrs)) {
        reportData = response.dlrs;
      } else {
        console.warn("No DLR data found in response");
        return;
      }

      setDLReport(reportData);
      setNextCursor(response.next_cursor || null);
      setCurrentCursor(cursorValue);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);

  // Handle next page
  const handleNextPage = async () => {
    if (nextCursor) {
      // Save current cursor to history before moving forward
      setPreviousCursors([...previousCursors, currentCursor]);
      setCurrentPage(currentPage + 1);
      await fetchData(nextCursor);
    }
  };

  // Handle previous page
  const handlePreviousPage = async () => {
    if (previousCursors.length > 0) {
      // Get the last cursor from history
      const newPreviousCursors = [...previousCursors];
      const lastCursor = newPreviousCursors.pop();

      setPreviousCursors(newPreviousCursors);
      setCurrentPage(currentPage - 1);
      await fetchData(lastCursor || null);
    }
  };

  // Check if campaign is in draft status
  const isDraft =
    campaign?.campaign_status === "DRAFT" ||
    campaign?.campaign_status === "NOT_STARTED";

  // Calculate metrics from campaign data and DLR data
  const metrics = useMemo(() => {
    if (!campaign) {
      return {
        sent: 0,
        delivered: 0,
        clicked: 0,
        failed: 0,
        dnd: 0,
      };
    }

    // Calculate metrics from DLR data
    const dlrMetrics = DLReport.reduce(
      (acc, report) => {
        if (report.status === "CLICKED") {
          acc.clicked++;
          acc.delivered++; // Clicked implies delivered
        } else if (report.status === "DELIVERED") {
          acc.delivered++;
        } else if (report.status === "FAILED") {
          acc.failed++;
        }
        return acc;
      },
      { delivered: 0, clicked: 0, failed: 0 }
    );

    return {
      sent: campaign.analytics.sent,
      delivered: dlrMetrics.delivered,
      clicked: dlrMetrics.clicked,
      failed: dlrMetrics.failed,
      dnd: campaign.analytics.dnd,
    };
  }, [campaign, DLReport]);

  const deliveryPercentage =
    campaign && campaign.analytics.attempted > 0
      ? ((metrics.delivered / campaign.analytics.attempted) * 100).toFixed(1)
      : "0.0";

  const clickPercentage =
    campaign && campaign.analytics.sent > 0
      ? ((metrics.clicked / campaign.analytics.sent) * 100).toFixed(1)
      : "0.0";

  const failurePercentage =
    campaign && campaign.analytics.attempted > 0
      ? ((metrics.failed / campaign.analytics.attempted) * 100).toFixed(1)
      : "0.0";

  // Animation effect
  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationStage((prev) => (prev < 4 ? prev + 1 : prev));
    }, 300);
    return () => clearTimeout(timer);
  }, [animationStage]);

  // Filter and search logic using actual DLReport data
  const filteredData = useMemo(() => {
    return DLReport.filter((record) => {
      const matchesSearch =
        record.phone_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.status.toLowerCase().includes(searchTerm.toLowerCase()) ||
        record.operator.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesFilter =
        statusFilter === "all" || record.status === statusFilter;

      return matchesSearch && matchesFilter;
    });
  }, [DLReport, searchTerm, statusFilter]);

  const exportToCSV = () => {
    const headers = [
      "Phone Number",
      "Status",
      "Operator",
      "Clicks",
      "First Click At",
      "Created At",
    ];
    const csvContent = [
      headers.join(","),
      ...filteredData.map((record) =>
        [
          record.phone_number,
          record.status,
          record.operator,
          record.clicks,
          record.first_click_at || "",
          record.created_at,
        ].join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sms_campaign_${id}_report.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const formatForDisplay = (encryptedValue: string): string => {
    if (!encryptedValue) return "N/A";
    if (encryptedValue.length <= 12) return encryptedValue;
    return `${encryptedValue.slice(0, 6)}...${encryptedValue.slice(-6)}`;
  };

  interface StatusBadgeProps {
    status: string;
  }

  const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
    const statusConfig: {
      [key: string]: {
        bg: string;
        text: string;
        icon: React.ElementType;
      };
    } = {
      DELIVERED: {
        bg: "bg-emerald-100",
        text: "text-emerald-800",
        icon: CheckCircle,
      },
      CLICKED: {
        bg: "bg-blue-100",
        text: "text-blue-800",
        icon: MousePointer,
      },
      FAILED: {
        bg: "bg-red-100",
        text: "text-red-800",
        icon: XCircle,
      },
      SENT: {
        bg: "bg-amber-100",
        text: "text-amber-800",
        icon: Clock,
      },
    };

    const config = statusConfig[status] || statusConfig["SENT"];
    const Icon = config.icon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${config.bg} ${config.text}`}
      >
        <Icon size={12} />
        {status.charAt(0).toUpperCase() + status.slice(1).toLowerCase()}
      </span>
    );
  };

  const MetricCard: React.FC<MetricCardProps> = ({
    title,
    value,
    percentage,
    icon: Icon,
    color,
    delay,
  }) => (
    <div
      className={`bg-white rounded-2xl p-6 border border-gray-100 hover:shadow-md transition-all duration-500 transform hover:-translate-y-1 ${animationStage >= delay
        ? "opacity-100 translate-y-0"
        : "opacity-0 translate-y-4"
        }`}
      style={{ transitionDelay: `${delay * 150}ms` }}
    >
      <div className="flex items-center justify-between mb-4">
        <div className={`p-3 rounded-xl ${color} bg-opacity-10`}>
          <Icon className={`w-6 h-6 ${color.replace("bg-", "text-")}`} />
        </div>
        <div
          className={`text-right ${color.replace(
            "bg-",
            "text-"
          )} font-semibold text-sm`}
        >
          {percentage}%
        </div>
      </div>
      <div className="space-y-1">
        <p className="text-3xl font-bold text-gray-900">
          {value?.toLocaleString()}
        </p>
        <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">
          {title}
        </p>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading campaign...</p>
        </div>
      </div>
    );
  }


  // Show draft campaign message
  if (isDraft) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-lg border border-gray-100 p-8 text-center">
          <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-10 h-10 text-amber-600" />
          </div>

          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Campaign Not Ready
          </h1>

          <p className="text-gray-600 mb-2">
            The campaign "
            <span className="font-semibold">{campaign.campaign_name}</span>" is
            still in{" "}
            <span className="font-semibold text-amber-600">
              {campaign.campaign_status}
            </span>{" "}
            status.
          </p>

          <p className="text-gray-500 text-sm mb-6">
            Campaign reports are only available for active or completed
            campaigns.
          </p>

          <div className="space-y-3">
            <button
              onClick={() => navigate("/campaign")}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-xl transition-colors duration-200"
            >
              Back to Campaigns
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div
          className={`bg-white rounded-3xl p-8 shadow border border-gray-100 transition-all duration-700 ${animationStage >= 0
            ? "opacity-100 translate-y-0"
            : "opacity-0 -translate-y-4"
            }`}
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl">
                  <MessageSquare className="w-8 h-8 text-white" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold text-gray-900">
                    {campaign?.campaign_name}
                  </h1>
                  <p className="text-lg text-gray-600 mt-1">
                    {campaign?.country}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap gap-6 text-sm text-gray-600">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  <span>
                    <strong>Start:</strong>{" "}

                    {campaign?.scheduled_start
                      ? new Date(campaign.scheduled_start).toLocaleString()
                      : "Not scheduled"}

                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>
                    <strong>End:</strong>{" "}
                    {campaign?.scheduled_end
                      ? new Date(campaign.scheduled_end).toLocaleString()
                      : "Not scheduled"}

                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <div
                className={`px-6 py-3 rounded-full font-semibold text-sm shadow-lg ${campaign?.campaign_status === "ACTIVE"
                  ? "bg-gradient-to-r from-green-500 to-emerald-600 text-white"
                  : campaign?.campaign_status === "COMPLETED"
                    ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white"
                    : campaign?.campaign_status === "PAUSED"
                      ? "bg-gradient-to-r from-yellow-500 to-yellow-600 text-white"
                      : "bg-gradient-to-r from-gray-500 to-gray-600 text-white"
                  }`}
              >
                {campaign?.campaign_status === "COMPLETED" ? "✓ " : ""}
                {campaign?.campaign_status}
              </div>
            </div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <MetricCard
            title="Messages Sent"
            value={analytics?.response?.sent ?? 0}
            percentage={100}
            icon={Zap}
            color="bg-blue-500"
            delay={1}
          />
          <MetricCard
            title="Delivered"
            value={analytics?.response?.delivered ?? 0}
            percentage={parseFloat(deliveryPercentage)}
            icon={CheckCircle}
            color="bg-emerald-500"
            delay={2}
          />
          <MetricCard
            title="Clicked"
            value={analytics?.response?.total_clicks ?? 0}
            percentage={parseFloat(clickPercentage)}
            icon={MousePointer}
            color="bg-amber-500"
            delay={3}
          />
          <MetricCard
            title="Failed"
            value={analytics?.response?.failed ?? 0}
            percentage={parseFloat(failurePercentage)}
            icon={XCircle}
            color="bg-red-500"
            delay={4}
          />
        </div>

        {/* Summary Stats */}
        <div
          className={`bg-white rounded-2xl p-8 border border-gray-100 transition-all duration-700 ${animationStage >= 4
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4"
            }`}
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
            <BarChart3 className="w-6 h-6 text-blue-600" />
            Campaign Performance Summary
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center space-y-2">
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics?.response?.delivered?.toLocaleString()}
              </h3>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Successful Deliveries
              </div>
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics?.response?.failed?.toLocaleString()}
              </h3>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Failed Messages
              </div>
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics?.response?.dnd?.toLocaleString()}
              </h3>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                DND Numbers
              </div>
            </div>
            <div className="text-center space-y-2">
              <h3 className="text-3xl font-bold text-gray-900">
                {analytics?.response?.total?.toLocaleString()}
              </h3>
              <div className="text-sm font-medium text-gray-500 uppercase tracking-wide">
                Campaign Size
              </div>
            </div>
          </div>
        </div>

        {/* DLR Table */}
        <div
          className={`bg-white rounded-2xl border border-gray-100 overflow-hidden transition-all duration-700 ${animationStage >= 4
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-4"
            }`}
        >
          {/* Table Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 p-6">
            <div className="flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex items-center gap-3 text-white">
                <Users className="w-6 h-6" />
                <h2 className="text-xl font-bold">
                  Delivery Report (DLR) Details
                </h2>
                <span className="bg-blue-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                  {filteredData.length} records
                </span>
              </div>

              <div className="flex flex-1 items-center gap-4">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    type="text"
                    placeholder="Search phone numbers, status, or operator..."
                    className="w-full pl-10 pr-4 py-2.5 bg-white bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-30"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <select
                  className="px-4 py-2.5 bg-black bg-opacity-10 border border-white border-opacity-20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-white "
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="DELIVERED">Delivered</option>
                  <option value="CLICKED">Clicked</option>
                  <option value="FAILED">Failed</option>
                  <option value="SENT">Sent</option>
                </select>

                <button
                  onClick={exportToCSV}
                  className="flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium transition-colors duration-200"
                >
                  <Download className="w-4 h-4" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Phone Number
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Operator
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Clicks
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    First Click At
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">
                    Created At
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredData.length === 0 ? (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-6 py-8 text-center text-gray-500"
                    >
                      {DLReport.length === 0
                        ? "No DLR data available"
                        : "No records match your search criteria"}
                    </td>
                  </tr>
                ) : (
                  filteredData.map((record, index) => (
                    <tr
                      key={`${record.message_id}-${index}`}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className="font-mono text-gray-900 text-xs"
                          title={`Encrypted phone: ${record.phone_number}`}
                        >
                          {formatForDisplay(record.phone_number)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <StatusBadge status={record.status} />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {record.operator}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {record.clicks || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {record.first_click_at
                          ? new Date(record.first_click_at).toLocaleString()
                          : "-"}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {new Date(record.created_at).toLocaleString()}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="px-6 py-4 border-t border-gray-200">
            {/* <Pagination
              currentPage={currentPage}
              hasNextPage={!!nextCursor}
              hasPrevPage={currentPage > 1}
              onNextPage={handleNextPage}
              onPrevPage={handlePreviousPage}
            /> */}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SMSCampaignReport;
